import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild,} from '@angular/core';

import {JsonPipe} from '@angular/common';
import {HttpClient} from '@angular/common/http';

import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatChipsModule} from '@angular/material/chips';
import {MatBadgeModule} from '@angular/material/badge';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {JsonFormsModule} from '@jsonforms/angular';
import {
  angularMaterialRenderers,
  JsonFormsAngularMaterialModule,
  JsonFormsDetailComponent,
} from '@jsonforms/angular-material';
import {and, isControl, optionIs, rankWith} from '@jsonforms/core';

import {NgxJsonViewerModule} from 'ngx-json-viewer';
import {cloneDeep, debounce, isEqual} from 'lodash';
import {BehaviorSubject, Subscription} from 'rxjs';


import {
  CustomJsonformsMaterialSelectComponent
} from "projects/nter-ui-kit/src/lib/webkit/components/vendor-specific/jsonforms/material/custom-jsonforms-material-select/custom-jsonforms-material-select.component";
import {
  buildEndpointFromTemplate,
  generateJsonFormsSchemas
} from "projects/nter-ui-kit/src/lib/webkit/components/crud-item/crud-item-decorator";
import {ActionItem} from "projects/nter-ui-kit/src/lib/webkit/components/common/crud-common-model";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {
  ConfirmationDialogComponent
} from "projects/nter-ui-kit/src/lib/webkit/components/confirmation-dialog/confirmation-dialog.component";
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from "@angular/material/form-field";

@Component({
  selector: 'app-crud-item',
  standalone: true,
  imports: [
    JsonFormsModule,
    JsonFormsAngularMaterialModule,
    JsonPipe,
    NgxJsonViewerModule,
    MatMenuModule,
    MatProgressBarModule,
    MatChipsModule,
    MatBadgeModule,
    MatSnackBarModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {floatLabel: 'always'}
    }
  ],
  templateUrl: './crud-item.component.html',
  styleUrl: './crud-item.component.scss',
})
export class CrudItemComponent implements OnInit, OnChanges {
  @Input() formData!: any;
  @Input() modelType!: any;
  @Input() additionalActions: ActionItem[] = [];
  @Input() apiEndpoint!: string;
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() longDescription?: string | null = null;
  @Input('data') initialData: any;
  @Input() submitMode: 'PERSIST' | 'LOCAL' = 'PERSIST';
  @Input() enableCancelButton: boolean = false;
  @Input() showLoaderBars = true;

  @Output('dataChange') initialDataChange = new EventEmitter<any>();
  @Output() onFormSubmitted = new EventEmitter<{
    data: any;
    success: boolean;
  }>();
  @Output() updateCanceled = new EventEmitter<boolean>();

  @ViewChild('jsonFormComponent') jsonFormComponent!: JsonFormsDetailComponent;

  public isLoading = false;
  public pendingChanges = false;
  public pendingChanges$ = new BehaviorSubject<boolean>(false);
  protected saveSubscription: Subscription | null = null;

  protected renderers = [
    ...angularMaterialRenderers,
    {
      renderer: CustomJsonformsMaterialSelectComponent,
      tester: rankWith(6, and(isControl, optionIs('customselect', true))),
    },
  ];

  protected jsonform: { jsonSchema?: any; uiSchema?: any } = {};
  protected formErrors: any = [];

  private debouncedHandleDataChange = debounce(this.handleDataChange, 300);
  private debouncedHandleErrors = debounce(this.handleErrors, 300);

  constructor(private http: HttpClient, private snackBar: MatSnackBar, private matDialog: MatDialog) {
    this.debouncedHandleDataChange = this.debouncedHandleDataChange.bind(this);
  }

  ngOnInit() {
    this.generateFormSchema();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && this.initialData) {
      this.formData = cloneDeep(this.initialData);
      this.generateForm();
    }
  }

  /**
   * Generates a JSON form UI schema and schema using the supplied class with the crud-item-decorators on each field.
   * Will also check, if the form has already been initialized and the method is called again, will reset the form to the initial data.
   */
  generateForm(data: any = this.initialData): void {
    this.formData = cloneDeep(data);
    this.pendingChanges = false;
    this.pendingChanges$.next(this.pendingChanges);
  }

  /**
   * Generates a JSON form UI schema and schema using the supplied class with the crud-item-decorators on each field.
   * Will also check, if the form has already been initialized and the method is called again, will reset the form to the initial data.
   */
  generateFormSchema(): void {
    this.jsonform = generateJsonFormsSchemas(this.modelType);
  }

  handleSubmit($event: any): void {
    if (this.submitMode === 'PERSIST') {
      this.handleSubmitPersist($event);
      return;
    }

    this.handleSubmitLocal();
  }

  handleSubmitPersist(data: any): void {
    this.isLoading = true;
    const endpoint = buildEndpointFromTemplate(this.apiEndpoint, this.formData);

    this.saveSubscription = this.http
      .post(endpoint, this.formData)
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.generateForm(response);
          this.snackBar.open('Update successful', 'Close', {duration: 3000});

          this.initialDataChange.emit(this.formData);
          this.onFormSubmitted.emit({data: this.formData, success: true});
          this.initialData = cloneDeep(this.formData);
        },
        error: (error) => {
          console.error('Error saving data:', error);
          this.isLoading = false;
          this.snackBar.open(
            'Error performing the update. Please try again.',
            'Close',
            {duration: 5000}
          );
        },
      });
  }

  handleSubmitLocal(): void {
    this.generateForm(this.formData);
    this.initialDataChange.emit(this.formData);
    this.onFormSubmitted.emit({data: this.formData, success: true});
    this.initialData = cloneDeep(this.formData);
  }

  cancelSave(): void {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
      this.isLoading = false;
    }
  }

  onDataChange($event: any): void {
    this.debouncedHandleDataChange($event);
  }

  private handleDataChange($event: any): void {
    this.pendingChanges = !isEqual(this.formData, this.initialData);
    this.pendingChanges$.next(this.pendingChanges);
  }

  handleReset(): void {
    this.generateForm();
  }

  onErrors($event: any) {
    this.debouncedHandleErrors($event);
  }

  private handleErrors($event: any) {
    this.formErrors = $event;
  }

  onAdditionalActionSelect(actionItem: ActionItem): void {
    const payload = actionItem.includeData ? {formData: this.formData} : {};
    actionItem.eventEmitter.emit(payload);
  }

  hasId(): boolean {
    return this.formData?.id || this.formData?.uuid;
  }

  handleUpdateCanceled($event: MouseEvent) {
    if (this.pendingChanges) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        message: 'There are unsaved changes. Are you sure you want to cancel abd discard all changes?',
        buttonConfirmLabel: 'Discard changes',
        buttonCancelLabel: 'Keep editing'
      };

      const dialogRef = this.matDialog.open(ConfirmationDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.handleReset();
          this.updateCanceled.emit(true);
        }
      });
    } else {
      this.handleReset();
      this.updateCanceled.emit(true);
    }
  }
}
